/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import RenderDocument from "App/render_document";
import RenderObject from "App/geometry/objects/object";
import IDataStorage from "App/storage/data_storage_interface";
import {DownloadManager} from "App/storage/download_manager";
import {DataResource, DataResourceState} from "App/storage/data_resource";
import {DataResourceObserver} from "App/storage/data_resource_observer";

/***
 * Responsible for handling all resources (textures, binary data), scheduling them to download and informing the observer that its data is downloaded
 * @param aDocument
 * @constructor
 */
export
class DataStorage implements IDataStorage {

    private static localDataUriGenerator: number = 1;

    private _document: RenderDocument;
    private _downloadManager: DownloadManager;
    private _resources: {} = {};

    constructor(aDocument: RenderDocument) {
        this._document = aDocument;
        this._downloadManager = new DownloadManager(this, this._document.getSceneAPI());
    }

    public createLocalResourceUri(): string {
        let uri = "localData(" + DataStorage.localDataUriGenerator++ + ")://";
        return uri;
    }

    public createLocalResourceFromArrayBuffer(aBuffer: ArrayBuffer) {
        let uri = "localData;buffer(" + DataStorage.localDataUriGenerator++ + ")://";

        // create resource for the uri
        let resource = this._resources[uri];
        if (!resource) {
            resource = new DataResource(this);
            this._resources[uri] = resource;
            resource.setProperties({
                url: uri,
                priority: 0,
                binSize: 0,
                required: false,
                localData: aBuffer
            });
            this._downloadManager.addResource(resource);
        }

        return uri;
    }

    public loadData(aObserver: DataResourceObserver): void {
        let loadDataQuery = aObserver.getLoadDataQuery();
        if (!loadDataQuery) {
            return;
        }

        // get/createMapping of url to id
        let resource = this._resources[loadDataQuery.url];
        if (!resource) {
            resource = new DataResource(this);

            resource.attachObserver(aObserver);

            this._resources[loadDataQuery.url] = resource;

            resource.setProperties(loadDataQuery);
            this._downloadManager.addResource(resource);

            this._document.getRenderer().getLoadStatus().requestBytes(resource);
        }
        else {
            resource.attachObserver(aObserver);
        }

        this._downloadManager.attachShapeBBoxes(resource.getUrl(), aObserver.getAttachedBoundingBoxes());
    }

    public removeDataResources(aDataResources: DataResource[]): void
    {
        for (let resource of aDataResources) {
            resource.detachAllObservers();

            switch (resource.getState()) {
                case DataResourceState.DOWNLOADING:
                    this._downloadManager.removeResource(resource.getUrl());
                    break;

                case DataResourceState.QUEUED:
                    resource.dataDownloadAborted(null, "queued");
                    this._downloadManager.removeResource(resource.getUrl());
                    break;
            }

            this._removeResource(resource);
        }
    }

    public updateShapeBBox(aShape: RenderObject): void
    {
        let resources = aShape.getAttachedDataResources();
        for (let resource of resources) {
            if (resource) {
                this._downloadManager.updateShapeBBox(
                    resource.getUrl(),
                    aShape.getUID(),
                    aShape.getBoundingBoxMin(),
                    aShape.getBoundingBoxMax()
                );
            }
        }
    }

    public removeShape(aShape: RenderObject): void
    {
        let resources = aShape.getAttachedDataResources();
        for (let resource of resources) {

            if (resource.getState() === DataResourceState.QUEUED) {
                this._downloadManager.detachShapeBBox(resource.getUrl(), aShape.getUID());
            }

            resource.detachObserver(aShape);

            // remove resource if there is no observer on it
            if (!resource.hasObservers()) {
                switch (resource.getState()) {
                    case DataResourceState.DOWNLOADING:
                        this._downloadManager.removeResource(resource.getUrl());
                        break;

                    case DataResourceState.QUEUED:
                        resource.dataDownloadAborted(null, "queued");
                        this._downloadManager.removeResource(resource.getUrl());
                        break;
                }

                this._removeResource(resource);
            }
        }
    }

    public getResource(aUrl: string): DataResource
    {
        let resource = this._resources[aUrl];
        if (resource === undefined) {
            return null;
        }

        return resource;
    }

    public resourceDownloaded(aResource: DataResource): void
    {
        this._removeResource(aResource);

        // notify the model statistics that geometry/texture was downloaded and schedule to recompute the resolution indexes as new texture is available
        this._document.getSceneAPI().viewModeModule.scheduleRedraw();
    }

    public resourceDownloadFailed(aResource: DataResource): void
    {

        this._removeResource(aResource);

        this._document.getSceneAPI().viewModeModule.scheduleRedraw();
    }

    public resourceDownloadAborted(aResource: DataResource): void
    {

        this._removeResource(aResource);

        this._document.getSceneAPI().viewModeModule.scheduleRedraw();
    }

    public getDownloadManager(): DownloadManager
    {
        return this._downloadManager;
    }

    public viewpointChanged(): void
    {
        this._downloadManager.viewpointChanged();
    }

    /***
     *
     * @param aResource resource object attached to e.g. shape which not necessarily be in this._resources. More shapes can
     *                  have one resource object attached to it.
     * @private
     */
    private _removeResource(aResource: DataResource): void
    {
        let resource = this._resources[aResource.getUrl()];
        if (resource !== undefined) {
            this._document.getRenderer().getLoadStatus().receiveBytes(resource, resource.getBinarySize());
            delete this._resources[aResource.getUrl()];
        }

        aResource.detachAllObservers();
    }
}
